/**
 * カート・チェックアウトの dataLayer プッシュ（GA4 / GTM 用）
 *
 * Shopify のカスタムピクセル / アプリ側でも同名イベントが送られている可能性があるため、
 * テーマ側のイベント名には fs_ を付ける（GTM 側で GA4 のイベント名に読み替える）。
 *
 * - fs_add_to_cart      : カートの中身が増えたとき（差分の items だけ）
 * - fs_remove_from_cart : カートの中身が減ったとき（差分の items だけ）
 * - fs_view_cart        : カートページ表示時・カートドロワーを開いたとき（cart_location: page / drawer）
 * - fs_begin_checkout   : チェックアウトボタン押下時（checkout_method: cart / accelerated / buy_now）
 *
 * 仕組み: component-datalayer-page.liquid が出力する window.fsCartSnapshot を起点に、
 * テーマの cart:change（detail.cart にカート全体が入る）と、/cart/add|change|update|clear への fetch / XHR
 * （アプリや個別スニペットからのカート操作）の完了後に /cart.js を取り直して差分を送る。
 * 通常のフォーム送信（ページ遷移を伴うカート追加）は、sessionStorage に残した前回の状態との差分で拾う。
 */
(() => {
  window.dataLayer = window.dataLayer || [];

  const STORAGE_KEY = 'fs_cart_snapshot';
  const CART_URL_RE = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const page = window.fsPageData || {};

  const listAttr = (productId) => (typeof window.fsListAttr === 'function' ? window.fsListAttr(productId) : {});

  // /cart.js のアイテムと Liquid 出力のアイテムを同じ形に揃える
  const normalize = (item) => ({
    product_id: item.product_id,
    variant_id: item.variant_id,
    product_title: item.product_title,
    variant_title: item.variant_title,
    vendor: item.vendor,
    product_type: item.product_type,
    sku: item.sku,
    price: typeof item.final_price === 'number' ? item.final_price / 100 : Number(item.price),
    quantity: item.quantity,
  });

  const toGaItem = (item) => ({
    item_id: String(item.product_id),
    item_name: item.product_title,
    item_brand: item.vendor,
    item_category: item.product_type,
    item_variant: String(item.variant_id),
    variant_title: item.variant_title,
    sku: item.sku,
    price: item.price,
    quantity: item.quantity,
    ...listAttr(item.product_id),
  });

  const sum = (items) => Math.round(items.reduce((t, i) => t + i.price * i.quantity, 0) * 100) / 100;

  const push = (event, items, extra = {}) => {
    if (!items.length) return;
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event,
      ...extra,
      ecommerce: { currency: snapshot.currency, value: sum(items), items: items.map(toGaItem) },
    });
  };

  // ---- スナップショット -------------------------------------------------
  let snapshot = window.fsCartSnapshot || { total: 0, currency: page.currency || 'JPY', items: [] };
  snapshot.items = snapshot.items.map(normalize);

  const fromCart = (cart) => ({
    total: cart.total_price / 100,
    currency: cart.currency || snapshot.currency,
    items: (cart.items || []).map(normalize),
  });

  const byVariant = (items) => {
    const map = {};
    items.forEach((i) => {
      const key = String(i.variant_id);
      map[key] = map[key] ? { ...map[key], quantity: map[key].quantity + i.quantity } : { ...i };
    });
    return map;
  };

  const diff = (prev, next) => {
    const a = byVariant(prev);
    const b = byVariant(next);
    const added = [];
    const removed = [];
    Object.keys(b).forEach((k) => {
      const d = b[k].quantity - (a[k] ? a[k].quantity : 0);
      if (d > 0) added.push({ ...b[k], quantity: d });
    });
    Object.keys(a).forEach((k) => {
      const d = a[k].quantity - (b[k] ? b[k].quantity : 0);
      if (d > 0) removed.push({ ...a[k], quantity: d });
    });
    return { added, removed };
  };

  const persist = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch (_) {}
  };

  // 直前のカート操作がどこから行われたか（cart_location に入れる）
  let lastLocation = '';
  const locationOf = (el) => {
    if (!el || !(el instanceof Element)) return '';
    if (el.closest('cart-drawer')) return 'drawer';
    if (el.closest('quick-buy-modal')) return 'quick_buy';
    if (el.closest('product-card')) return 'product_card';
    if (el.closest('[data-section-type="main-cart"], .cart-form, cart-notification')) return 'cart_page';
    if (el.closest('.product-info, product-form, [id^="product-form-"]')) return 'product_page';
    return 'other';
  };
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target instanceof Element ? e.target : null;
      const el = target && target.closest('button, input[type="submit"], a, quantity-selector, line-item-quantity');
      if (el) lastLocation = locationOf(el);
    },
    true
  );
  document.addEventListener('submit', (e) => (lastLocation = locationOf(e.target)), true);

  const apply = (next, { emitRemoved = true, cartLocation } = {}) => {
    const { added, removed } = diff(snapshot.items, next.items);
    snapshot = next;
    persist();
    const extra = { cart_location: cartLocation || lastLocation || (page.template === 'cart' ? 'cart_page' : 'other') };
    push('fs_add_to_cart', added, extra);
    if (emitRemoved) push('fs_remove_from_cart', removed, extra);
    if (added.length || removed.length) lastLocation = '';
  };

  // ページ遷移を伴うカート追加（通常のフォーム送信・別タブでの追加）を、前回の状態との差分で拾う。
  // 購入完了後などカートが空になっているケースを削除扱いにしないため、ページ読み込み時は追加分だけ送る。
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (stored && Array.isArray(stored.items)) {
      const current = snapshot;
      snapshot = stored;
      apply(current, { emitRemoved: false, cartLocation: 'page_load' });
    } else {
      persist();
    }
  } catch (_) {
    persist();
  }

  // ---- カート更新を拾って差分を送る（直列化して二重送信を防ぐ） -------------
  let queue = Promise.resolve();
  const enqueue = (task) => {
    queue = queue.then(task).catch(() => {});
    return queue;
  };
  const refresh = () =>
    enqueue(() =>
      fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => apply(fromCart(cart)))
    );

  // テーマの cart:change はカート全体を持っているので取り直さずに使う
  document.addEventListener('cart:change', (e) => {
    const cart = e.detail && e.detail.cart;
    if (cart && Array.isArray(cart.items)) enqueue(() => apply(fromCart(cart)));
    else refresh();
  });
  document.addEventListener('cart:refresh', () => refresh());

  // fetch / XHR でカート API が呼ばれたら、完了後に取り直す（アプリや個別スニペットからの操作も拾う）
  const isCartUrl = (url) => {
    try {
      return CART_URL_RE.test(new URL(String(url), location.href).pathname);
    } catch (_) {
      return false;
    }
  };
  if (window.fetch) {
    const origFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' || input instanceof URL ? input : input && input.url;
      const p = origFetch.apply(this, arguments);
      if (isCartUrl(url)) p.then(() => refresh(), () => {});
      return p;
    };
  }
  if (window.XMLHttpRequest) {
    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (isCartUrl(url)) this.addEventListener('loadend', () => refresh());
      return origOpen.apply(this, arguments);
    };
  }

  // ---- fs_view_cart --------------------------------------------------------
  const viewCart = (cartLocation) => {
    // 空カートでも閲覧自体は計測したいので items が無い場合も送る
    if (!snapshot.items.length) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'fs_view_cart',
        cart_location: cartLocation,
        cart_empty: true,
        ecommerce: { currency: snapshot.currency, value: 0, items: [] },
      });
      return;
    }
    push('fs_view_cart', snapshot.items, { cart_location: cartLocation, cart_empty: false });
  };
  if (page.template === 'cart') viewCart('page');
  // dialog:after-show はバブリングしないためキャプチャで拾う
  document.addEventListener(
    'dialog:after-show',
    (e) => {
      if (e.target instanceof Element && e.target.matches('cart-drawer')) enqueue(() => viewCart('drawer'));
    },
    true
  );

  // ---- fs_begin_checkout ---------------------------------------------------
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      const button = target.closest('button[name="checkout"], input[name="checkout"]');
      if (button && !button.disabled) {
        push('fs_begin_checkout', snapshot.items, {
          cart_location: button.closest('cart-drawer') ? 'drawer' : 'page',
          checkout_method: 'cart',
        });
        return;
      }
      // カートの Shop Pay / Apple Pay 等の高速チェックアウト
      if (target.closest('.additional-checkout-buttons')) {
        push('fs_begin_checkout', snapshot.items, {
          cart_location: target.closest('cart-drawer') ? 'drawer' : 'page',
          checkout_method: 'accelerated',
        });
        return;
      }
      // 商品詳細の「今すぐ購入」（動的チェックアウトボタン）はカートを経由しないので、その商品だけで送る
      if (target.closest('.shopify-payment-button') && page.product) {
        const p = page.product;
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'fs_begin_checkout',
          cart_location: 'product_page',
          checkout_method: 'buy_now',
          ecommerce: {
            currency: snapshot.currency,
            value: p.price,
            items: [{ ...p, quantity: 1, ...listAttr(p.item_id) }],
          },
        });
      }
    },
    true
  );
})();
